<template>
  <div class="relative">
    <div
      v-if="loading"
      class="absolute inset-0 bg-white/80 z-10 flex items-center justify-center rounded"
    >
      <svg
        aria-hidden="true"
        class="w-6 h-6 text-gray-500 animate-spin"
        fill="none"
        role="status"
        viewBox="0 0 100 101"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
          fill="#E5E7EB"
        />
        <path
          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
          fill="currentColor"
        />
      </svg>
    </div>
    <div class="border border-gray-300 border-solid rounded text-center">
      <div class="flex items-center p-2">
        <button
          class="-m-1.5 flex flex-none items-center justify-center p-1.5 bks"
          type="button"
          @click="back"
        >
          <span class="t">{{ $t('Previous month') }}</span>
          <svg
            aria-hidden="true"
            class="w-5 h-5"
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              clip-rule="evenodd"
              d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
              fill-rule="evenodd"
            ></path>
          </svg>
        </button>
        <div class="ui text-sm font-semibold">
          {{ $t(startOfMonthOfSelectedDate.toFormat('MMMM')) }}
          {{ startOfMonthOfSelectedDate.toFormat('yyyy') }}
        </div>
        <button
          class="-m-1.5 flex flex-none items-center justify-center p-1.5 bks"
          type="button"
          @click="forward"
        >
          <span class="t">{{ $t('Next month') }}</span>
          <svg
            aria-hidden="true"
            class="w-5 h-5"
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              clip-rule="evenodd"
              d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
              fill-rule="evenodd"
            ></path>
          </svg>
        </button>
      </div>
      <div class="p-1.5">
        <div class="grid grid-cols-7">
          <div v-for="i in weekDayAbbreviations" class="font-semibold">
            {{ i }}
          </div>
        </div>
        <div class="grid grid-cols-7">
          <div
            v-for="i in forRender"
            :class="{
              'font-bold bg-blue-50':
                i.dateTime.toFormat('d-L-yy') === modelValue.toFormat('d-L-yy'),
              'font-bold':
                i.dateTime.toFormat('d-L-yy') ===
                DateTime.now().toFormat('d-L-yy'),
              '!cursor-not-allowed text-gray-300': i.disabled,
              'text-gray-400':
                i.dateType === DateType.PREVIOUS_MONTH ||
                i.dateType === DateType.NEXT_MONTH,
            }"
            class="cursor-pointer p-2 rounded hover:!bg-indigo-100"
            @click="!i.disabled && selectDate(i.dateTime)"
          >
            <slot :date="i.dateTime" :day="i.day" name="day">
              {{ i.day }}
            </slot>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { DateTime } from 'luxon'
import { onMounted } from 'vue'

export interface Props {
  blocked?: [] | [DateTime]
  available?: [] | [string] | null
  disablePastDates: boolean
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  blocked: [],
  disablePastDates: false,
  loading: false,
})

const modelValue = defineModel<DateTime>('modelValue', {
  default: () => DateTime.now(),
})
const baseDateTime = defineModel<DateTime>('baseDateTime', {
  default: () => DateTime.now(),
})

const hasAvailableDatesArrayBeenProvided = computed(() => !!props.available)

const { t } = useI18n()
const weekDayAbbreviations = [
  t('Mo'),
  t('Tu'),
  t('We'),
  t('Th'),
  t('Fr'),
  t('Sa'),
  t('Su'),
]

onMounted(() => {
  if (hasAvailableDatesArrayBeenProvided.value) {
    const availableDates = props.available
      ?.map((date) => DateTime.fromFormat(date, 'yyyy-LL-dd'))
      ?.sort()
    if (availableDates?.length) {
      baseDateTime.value = availableDates[0]
    }
  }
})

const startOfMonthOfSelectedDate = computed(() =>
  baseDateTime.value.startOf('month'),
)
const endOfMonthOfSelectedDate = computed(() =>
  baseDateTime.value.endOf('month'),
)

const daysInLastMonth = computed(
  () => startOfMonthOfSelectedDate.value.minus({ month: 1 }).daysInMonth,
)
const daysInCurrentMonth = computed(
  () => startOfMonthOfSelectedDate.value.daysInMonth,
)
const daysInNextMonth = computed(
  () => startOfMonthOfSelectedDate.value.plus({ month: 1 }).daysInMonth,
)

const daysInPreviousMonthArr = computed(() =>
  Array(daysInLastMonth.value)
    .fill()
    .map((_, i) => i + 1),
)
const daysInCurrentMonthArr = computed(() =>
  Array(daysInCurrentMonth.value)
    .fill()
    .map((_, i) => i + 1),
)
const daysInNextMonthArr = computed(() =>
  Array(daysInNextMonth.value)
    .fill()
    .map((_, i) => i + 1),
)

// Not sure if these are optimal, error resistant
const daysOnCalendarFromPerviousMonth = computed(() =>
  startOfMonthOfSelectedDate.value.weekday > 1
    ? daysInPreviousMonthArr.value.slice(
        -Math.max(0, startOfMonthOfSelectedDate.value.weekday - 1),
      )
    : [],
)
const daysOnCalendarFromNextMonth = computed(() =>
  daysInNextMonthArr.value.slice(0, 7 - endOfMonthOfSelectedDate.value.weekday),
)

const calendarDatesObjects = computed(() => [
  ...daysOnCalendarFromPerviousMonth.value.map((day) =>
    DateTime.local(
      startOfMonthOfSelectedDate.value.minus({ month: 1 }).year,
      startOfMonthOfSelectedDate.value.minus({ month: 1 }).month,
      day,
    ),
  ),
  ...daysInCurrentMonthArr.value.map((day) =>
    DateTime.local(
      startOfMonthOfSelectedDate.value.year,
      startOfMonthOfSelectedDate.value.month,
      day,
    ),
  ),
  ...daysOnCalendarFromNextMonth.value.map((day) =>
    DateTime.local(
      startOfMonthOfSelectedDate.value.plus({ month: 1 }).year,
      startOfMonthOfSelectedDate.value.plus({ month: 1 }).month,
      day,
    ),
  ),
])

enum DateType {
  PREVIOUS_MONTH = 'PREVIOUS_MONTH',
  CURRENT_MONTH = 'CURRENT_MONTH',
  NEXT_MONTH = 'NEXT_MONTH',
}

const forRender:
  | []
  | [
      {
        dateTime: null | DateTime
        day: number
        dateType: DateType
        disabled: boolean
      },
    ] = computed(() => [
  ...daysOnCalendarFromPerviousMonth.value.map((day) => {
    const dateTimeObj = DateTime.local(
      startOfMonthOfSelectedDate.value.minus({ month: 1 }).year,
      startOfMonthOfSelectedDate.value.minus({ month: 1 }).month,
      day,
    )
    return {
      dateTime: dateTimeObj || null,
      day,
      dateType: DateType.PREVIOUS_MONTH,
      disabled:
        // bending to the existing format of old booking page
        (props.available?.length
          ? !props.available?.includes(dateTimeObj?.toFormat('yyyy-LL-dd'))
          : hasAvailableDatesArrayBeenProvided.value) ||
        (props.disablePastDates &&
          dateTimeObj.startOf('day') < DateTime.now().startOf('day')) ||
        props.blocked.some(
          (blockedDate) =>
            blockedDate?.toFormat('d-L-yy') == dateTimeObj?.toFormat('d-L-yy'),
        ),
    }
  }),
  ...daysInCurrentMonthArr.value.map((day) => {
    const dateTimeObj = DateTime.local(
      startOfMonthOfSelectedDate.value.year,
      startOfMonthOfSelectedDate.value.month,
      day,
    )
    return {
      dateTime: dateTimeObj,
      day,
      dateType: DateType.CURRENT_MONTH,
      disabled:
        // bending to the existing format of old booking page
        (props.available?.length
          ? !props.available?.includes(dateTimeObj?.toFormat('yyyy-LL-dd'))
          : hasAvailableDatesArrayBeenProvided.value) ||
        (props.disablePastDates &&
          dateTimeObj.startOf('day') < DateTime.now().startOf('day')) ||
        props.blocked.some(
          (blockedDate) =>
            blockedDate?.toFormat('d-L-yy') == dateTimeObj?.toFormat('d-L-yy'),
        ),
    }
  }),
  ...daysOnCalendarFromNextMonth.value.map((day) => {
    const dateTimeObj = DateTime.local(
      startOfMonthOfSelectedDate.value.plus({ month: 1 }).year,
      startOfMonthOfSelectedDate.value.plus({ month: 1 }).month,
      day,
    )
    return {
      dateTime: dateTimeObj,
      day,
      dateType: DateType.NEXT_MONTH,
      disabled:
        // bending to the existing format of old booking page
        (props.available?.length
          ? !props.available?.includes(dateTimeObj?.toFormat('yyyy-LL-dd'))
          : hasAvailableDatesArrayBeenProvided.value) ||
        (props.disablePastDates &&
          dateTimeObj.startOf('day') < DateTime.now().startOf('day')) ||
        props.blocked.some(
          (blockedDate) =>
            blockedDate?.toFormat('d-L-yy') == dateTimeObj?.toFormat('d-L-yy'),
        ),
    }
  }),
])

const emit = defineEmits(['update-month-year'])
const back = () => {
  const updatedDateTime = baseDateTime.value.minus({ month: 1 })
  baseDateTime.value = updatedDateTime
  emit('update-month-year', {
    month: updatedDateTime.month,
    year: updatedDateTime.year,
  })
}
const forward = () => {
  const updatedDateTime = baseDateTime.value.plus({ month: 1 })
  baseDateTime.value = updatedDateTime
  emit('update-month-year', {
    month: updatedDateTime.month,
    year: updatedDateTime.year,
  })
}

const selectDate = (date: DateTime) => {
  modelValue.value = date
}
</script>

<style>
.t {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

.ui {
  flex: 1 1 auto;
}
</style>
